import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronDownIcon, FacebookIcon, InstagramIcon, LinkIcon, MinusIcon, PlusIcon, ShieldCheckIcon, ShoppingBagIcon, StarIcon, TruckIcon, TwitterIcon } from 'lucide-react';
import { getProductBySlug, PRODUCTS } from '../data/products';
import { formatPrice, stockLabel } from '../utils/format';
import { useCart } from '../contexts/CartContext';
import { ProductCard } from '../components/ProductCard';
const SHARE = [{
  label: 'Compartir en Instagram',
  icon: InstagramIcon
}, {
  label: 'Compartir en Facebook',
  icon: FacebookIcon
}, {
  label: 'Compartir en X',
  icon: TwitterIcon
}, {
  label: 'Copiar enlace',
  icon: LinkIcon
}];
export function ProductDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = slug ? getProductBySlug(slug) : undefined;
  const {
    addItem
  } = useCart();
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState(0);
  const [colorId, setColorId] = useState(product?.colors[0]?.id ?? 'black');
  const [quantity, setQuantity] = useState(1);
  const [openSection, setOpenSection] = useState<string | null>('desc');
  const [zoom, setZoom] = useState<{
    x: number;
    y: number;
  } | null>(null);
  React.useEffect(() => {
    setActiveImage(0);
    setQuantity(1);
    setColorId(product?.colors[0]?.id ?? 'black');
    setOpenSection('desc');
  }, [slug, product]);
  const related = useMemo(() => {
    const others = PRODUCTS.filter(p => p.id !== product?.id);
    const sameCategory = others.filter(p => p.category === product?.category);
    const rest = others.filter(p => p.category !== product?.category);
    return [...sameCategory, ...rest].slice(0, 4);
  }, [product]);
  if (!product) {
    return <div className="mx-auto max-w-3xl px-5 py-32 text-center" data-id="element-1177" data-mp="2i6nkr6cbn1ze">
        <h1 className="font-display text-5xl uppercase text-white" data-id="element-1178" data-mp="2i99o0ts6ivbt">
          Producto no encontrado
        </h1>
        <Link to="/productos" className="mt-8 inline-block bg-pink-500 px-8 py-4 text-[11px] font-bold uppercase tracking-[0.18em] text-white" data-id="element-1179" data-mp="2345lfqxi7jjl">
          Volver al catálogo
        </Link>
      </div>;
  }
  const stock = stockLabel(product.stock);
  const soldOut = product.stock === 0;
  const price = product.salePrice ?? product.price;
  const selectedColor = product.colors.find(c => c.id === colorId);
  const sections = [{
    id: 'desc',
    title: 'Descripción completa',
    body: product.description
  }, {
    id: 'how',
    title: 'Cómo usar',
    body: product.howToUse
  }, {
    id: 'ship',
    title: 'Envío y devoluciones',
    body: 'Envío gratis en compras mayores a $ 3.500. Entregas en Montevideo en 24-48 h y al interior en 2-4 días hábiles vía DAC. Tenés 30 días para cambios o devoluciones sin uso, con el embalaje original.'
  }];
  return <div className="w-full bg-black" data-id="element-1180" data-mp="30rxm4uvrt2vx">
      {/* BREADCRUMB */}
      <nav aria-label="Ruta de navegación" className="mx-auto w-full max-w-7xl px-5 pt-8 text-[11px] uppercase tracking-[0.16em] text-white/35 lg:px-8" data-id="element-1181" data-mp="30sgrlc48hyt2">
        <Link to="/" className="hover:text-white" data-id="element-1182" data-mp="3d9hug3t19rof">
          Inicio
        </Link>
        <span className="mx-2" data-id="element-1183" data-mp="3i0q5t5dxbn48">/</span>
        <Link to="/productos" className="hover:text-white" data-id="element-1184" data-mp="3i1rnvttcx9vg">
          Productos
        </Link>
        <span className="mx-2" data-id="element-1185" data-mp="3mztauptwltc7">/</span>
        <span className="text-white/70" data-id="element-1186" data-mp="2pckiqbzedm18">{product.name}</span>
      </nav>

      <div className="mx-auto grid w-full max-w-7xl gap-12 px-5 py-10 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-14" data-id="element-1187" data-mp="2pef6rz5ytu9j">
        {/* LEFT: GALLERY */}
        <div data-id="element-1188" data-mp="2za8nluqpw9x4">
          <div className="relative aspect-square w-full overflow-hidden border border-white/10 bg-ink-800" onMouseMove={e => {
          const rect = e.currentTarget.getBoundingClientRect();
          setZoom({
            x: (e.clientX - rect.left) / rect.width * 100,
            y: (e.clientY - rect.top) / rect.height * 100
          });
        }} onMouseLeave={() => setZoom(null)} data-id="element-1189" data-mp="2z9p62f4zbnjm">
            <img src={product.images[activeImage]} alt={product.name} className="h-full w-full object-cover transition-transform duration-200" style={zoom ? {
            transform: 'scale(1.9)',
            transformOrigin: `${zoom.x}% ${zoom.y}%`
          } : undefined} data-id="element-1190" data-mp="c5w2c5un13p1" />
            {product.salePrice && <span className="absolute left-4 top-4 bg-pink-500 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white" data-id="element-1191" data-mp="v2noqo5h7yxb">
                -
                {Math.round((product.price - product.salePrice) / product.price * 100)}
                %
              </span>}
            <span className="pointer-events-none absolute bottom-4 right-4 bg-black/70 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-white/60" data-id="element-1192" data-mp="1et9881yo3rmj">
              Pasá el mouse para ampliar
            </span>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-3" data-id="element-1193" data-mp="22etb784kx0sj">
            {product.images.map((img, i) => <button key={img + i} type="button" onClick={() => setActiveImage(i)} aria-label={`Ver imagen ${i + 1}`} aria-pressed={activeImage === i} className={`aspect-square overflow-hidden border transition-colors ${activeImage === i ? 'border-pink-500' : 'border-white/10 hover:border-white/40'}`} data-id="element-1194" data-mp="1ofto44wpstyh" data-mpi={i}>
                <img src={img} alt="" className="h-full w-full object-cover opacity-80 transition-opacity hover:opacity-100" data-id="element-1195" data-mp="19s8km9vxgsq1" />
              </button>)}
          </div>
        </div>

        {/* RIGHT: INFO */}
        <div data-id="element-1196" data-mp="32gq44ccfo6gk">
          <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-pink-500" data-id="element-1197" data-mp="32hhnmosradqh">
            {product.categoryLabel}
          </span>
          <h1 className="mt-3 font-display text-5xl uppercase leading-none text-white sm:text-6xl" data-id="element-1198" data-mp="2tnd2mm0azwiv">
            {product.name}
          </h1>

          <div className="mt-4 flex items-center gap-4" data-id="element-1199" data-mp="2optaqjaonl8g">
            <div className="flex items-center gap-1" aria-label={`${product.rating} de 5`} data-id="element-1200" data-mp="3leiukf5venx2">
              {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className={`h-4 w-4 ${i < Math.round(product.rating) ? 'fill-pink-500 text-pink-500' : 'text-white/20'}`} data-id="element-1201" data-mp="3heihaxuicirl" />)}
            </div>
            <span className="text-xs text-white/45" data-id="element-1202" data-mp="210ihrnzkqv4v">
              {product.rating} · {product.reviews} reseñas
            </span>
            <span className="flex items-center gap-1.5 text-xs" data-id="element-1203" data-mp="1lvxnrb8nsve0">
              <span className={`h-2 w-2 rounded-full ${stock.dot}`} aria-hidden data-id="element-1204" data-mp="1lxh5z7dvlgif" />
              <span className={stock.text} data-id="element-1205" data-mp="1qvsenesfpkz2">{stock.label}</span>
            </span>
          </div>

          <div className="mt-6 flex items-baseline gap-3" data-id="element-1206" data-mp="1vt29j9yleoua">
            <span className={`font-display text-5xl leading-none ${product.salePrice ? 'text-pink-500' : 'text-white'}`} data-id="element-1207" data-mp="20ktvt8apu8em">
              {formatPrice(price)}
            </span>
            {product.salePrice && <span className="text-lg text-white/35 line-through" data-id="element-1208" data-mp="29nl8fbfbva7u">
                {formatPrice(product.price)}
              </span>}
          </div>
          <p className="mt-2 text-xs text-white/40" data-id="element-1209" data-mp="2jimxk79lscaf">
            3 pagos sin interés de {formatPrice(Math.round(price / 3))}
          </p>

          <p className="mt-6 text-base leading-relaxed text-white/55" data-id="element-1210" data-mp="k8n77pxfhr5p">
            {product.shortDescription}
          </p>

          {/* COLOR */}
          <div className="mt-9" data-id="element-1211" data-mp="2r2fcsiuhj8x5">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white" data-id="element-1212" data-mp="2r1co8hkmxike">
              Color:{' '}
              <span className="font-medium text-white/50" data-id="element-1213" data-mp="2m1zz1q6ajzen">
                {selectedColor?.name}
              </span>
            </h2>
            <div className="mt-4 flex gap-3" data-id="element-1214" data-mp="2vtdwztilgr5o">
              {product.colors.map(color => <button key={color.id} type="button" onClick={() => setColorId(color.id)} aria-label={color.name} aria-pressed={colorId === color.id} className={`flex h-11 w-11 items-center justify-center rounded-full border transition-all ${colorId === color.id ? 'border-white' : 'border-white/20 hover:border-white/50'}`} data-id="element-1215" data-mp="283tax1r59ds6">
                  <span className="h-7 w-7 rounded-full" style={{
                backgroundColor: color.hex
              }} data-id="element-1216" data-mp="3ohtbmextd7gh" />
                </button>)}
            </div>
          </div>

          {/* QUANTITY */}
          <div className="mt-8" data-id="element-1217" data-mp="3m4687q9e1xv6">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white" data-id="element-1218" data-mp="3m57vct3oiat9">
              Cantidad
            </h2>
            <div className="mt-4 inline-flex items-center border border-white/15 bg-ink-800" data-id="element-1219" data-mp="3r53nnv30kdsz">
              <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))} aria-label="Quitar una unidad" className="p-3.5 text-white/60 transition-colors hover:bg-white/5 hover:text-white" data-id="element-1220" data-mp="inzdi98ybayj">
                <MinusIcon className="h-4 w-4" data-id="element-1221" data-mp="duwl44fwu7jx" />
              </button>
              <span className="w-14 text-center font-display text-xl text-white" data-id="element-1222" data-mp="11ltimc6yl2bn">
                {quantity}
              </span>
              <button type="button" onClick={() => setQuantity(q => q + 1)} aria-label="Agregar una unidad" className="p-3.5 text-white/60 transition-colors hover:bg-white/5 hover:text-white" data-id="element-1223" data-mp="16ecwzbdh01hi">
                <PlusIcon className="h-4 w-4" data-id="element-1224" data-mp="1dvtlkb784eu1" />
              </button>
            </div>
          </div>

          {/* CTAS */}
          <div className="mt-8 space-y-3" data-id="element-1225" data-mp="1iu4u8ils8jao">
            <button type="button" disabled={soldOut} onClick={() => {
            addItem(product.id, colorId, quantity);
            navigate('/carrito');
          }} className={`flex w-full items-center justify-center gap-2 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${soldOut ? 'cursor-not-allowed bg-ink-600 text-white/35' : 'bg-pink-500 text-white hover:bg-pink-600'}`} data-id="element-1226" data-mp="147bn40mf2jtk">
              <ShoppingBagIcon className="h-4 w-4" data-id="element-1227" data-mp="q6slpccgic0d" />
              {soldOut ? 'Sin stock' : 'Agregar al carrito'}
            </button>
            <Link to="/checkout" onClick={() => !soldOut && addItem(product.id, colorId, quantity)} className="block w-full border border-white py-4 text-center text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-white hover:text-black" data-id="element-1228" data-mp="2jx0508fxqj7">
              Comprar ahora
            </Link>
          </div>

          <ul className="mt-8 grid gap-3 border-y border-white/10 py-6 text-sm text-white/50 sm:grid-cols-2" data-id="element-1229" data-mp="pia2jcr41g5w">
            <li className="flex items-center gap-3" data-id="element-1230" data-mp="za6argmwumnu">
              <TruckIcon className="h-4 w-4 text-teal-500" data-id="element-1231" data-mp="z8uxmq7ks2k2" />
              Envío gratis desde $ 3.500
            </li>
            <li className="flex items-center gap-3" data-id="element-1232" data-mp="ubuzd1gyw43i">
              <ShieldCheckIcon className="h-4 w-4 text-teal-500" data-id="element-1233" data-mp="197kf4hvk7kzs" />
              Garantía de 12 meses
            </li>
          </ul>

          {/* ACCORDION */}
          <div className="mt-8 border-t border-white/10" data-id="element-1234" data-mp="147fvwk39oc5v">
            {sections.map(section => {
            const open = openSection === section.id;
            return <div key={section.id} className="border-b border-white/10" data-id="element-1235" data-mp="1gbtm37wkjrkw">
                  <h2 data-id="element-1236" data-mp="1l9v6px9n7yyh">
                    <button type="button" onClick={() => setOpenSection(open ? null : section.id)} aria-expanded={open} className="flex w-full items-center justify-between gap-4 py-5 text-left font-display text-lg uppercase tracking-[0.14em] text-white" data-id="element-1237" data-mp="1l9bzpblj6uz8">
                      {section.title}
                      <ChevronDownIcon className={`h-4 w-4 shrink-0 text-pink-500 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} data-id="element-1238" data-mp="23z1sxu9nb8p1" />
                    </button>
                  </h2>
                  {open && <p className="pb-6 text-sm leading-relaxed text-white/55" data-id="element-1239" data-mp="72p4fx68zp3i">
                      {section.body}
                    </p>}
                </div>;
          })}
          </div>

          {/* SHARE */}
          <div className="mt-8 flex items-center gap-4" data-id="element-1240" data-mp="3tj1l6wysz2c6">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40" data-id="element-1241" data-mp="3ojfbgxi6o484">
              Compartir
            </span>
            <div className="flex gap-2" data-id="element-1242" data-mp="q08ft7g6hjt3">
              {SHARE.map(({
              label,
              icon: Icon
            }) => <button key={label} type="button" aria-label={label} className="flex h-9 w-9 items-center justify-center border border-white/15 text-white/60 transition-colors hover:border-pink-500 hover:bg-pink-500 hover:text-white" data-id="element-1243" data-mp="l3roy5hlgrcb">
                  <Icon className="h-4 w-4" data-id="element-1244" data-mp="30rhz95atna6h" />
                </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* RELATED */}
      <section className="border-t border-white/10 bg-ink-900" data-id="element-1245" data-mp="oi9q78hmqxgo">
        <div className="mx-auto w-full max-w-7xl px-5 py-16 lg:px-8 lg:py-24" data-id="element-1246" data-mp="t9ri771odnpi">
          <h2 className="font-display text-4xl uppercase leading-none text-white sm:text-5xl" data-id="element-1247" data-mp="tbuiql0gez8h">
            Productos relacionados
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1248" data-mp="jjewvcm1dqib">
            {related.map(item => <ProductCard key={item.id} product={item} data-id="element-1249" data-mp="17b3vtbtd5d88" />)}
          </div>
        </div>
      </section>
    </div>;
}